import React, { useState } from 'react';
import { Card, Form, message } from 'antd';

import './index.scss';
import InputComponent from '@/components/Input';
import ButtonComponent from '@/components/Button';
import { ReactComponent as PlusIcon } from '@/assets/images/icons/plus.svg';
import { ReactComponent as TrashIcon } from '@/assets/images/icons/trash.svg';
import { ReactComponent as CopyIcon } from '@/assets/images/icons/copy.svg';
import { useTranslation } from 'react-i18next';
import { validateIPaddress } from '@/utils/validate';
import { uniqueId } from 'lodash';
import { generateAPIKey, getIPs, updateIPs } from '@/services/apiService';
import { useBeforeRender } from '@/hooks/useBeforeRender';
import { ENV } from '@/constants';

function Authentication() {
  const translation = useTranslation().t;
  const [addresses, setAddresses] = useState([]);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [APIKey, setApiKey] = useState('');
  const [loadingAddIp, setLoadingAddIp] = useState(false);

  const updateAddress = async (ips) => {
    setLoadingAddIp(true);
    const { success } = await updateIPs({ whitelistIps: ips });
    if (success) {
      setAddresses(ips);
    }
    setLoadingAddIp(false);
  };

  const onAddIP = (values) => {
    const ips = [...addresses, values.ipAddress];
    updateAddress(ips);
    form.resetFields();
  };

  const handleRemoveIP = (index) => {
    addresses.splice(index, 1);
    updateAddress([...addresses]);
  };

  const copyToken = (text) => {
    navigator.clipboard.writeText(text);
    message.success(translation('api_page.authentication.copy_token'));
  };

  const fetchData = async () => {
    const { success, data } = await getIPs();
    if (success) {
      const { whitelistIps } = data;
      setAddresses([...whitelistIps]);
    }
  };

  const genAPIKey = async () => {
    setLoading(true);
    const { success, data } = await generateAPIKey();
    if (success) {
      const { apiKey } = data;
      setApiKey(apiKey);
    }
    setLoading(false);
  };

  useBeforeRender(() => fetchData(), []);

  return (
    <>
      <Card
        className="api-authentication"
        title={translation('api_page.authentication.whilelist_ip')}
      >
        <div className="sms-input__label">
          {translation('api_page.authentication.whilelist_ip')}
        </div>
        <Form
          name="filter"
          layout="vertical"
          onFinish={onAddIP}
          autoComplete="off"
          className="form"
          form={form}
        >
          <div className="api-authentication__whitelist">
            <Form.Item
              name="ipAddress"
              rules={[
                {
                  required: true,
                  message: translation(
                    'api_page.authentication.form.errors.whitelist.required',
                  ),
                },
                () => ({
                  validator(_, value) {
                    if (value && !validateIPaddress(value)) {
                      return Promise.reject(
                        new Error(
                          translation(
                            'api_page.authentication.form.errors.whitelist.invalid',
                          ),
                        ),
                      );
                    }
                    if (value && addresses.includes(value)) {
                      return Promise.reject(
                        new Error(
                          translation(
                            'api_page.authentication.form.errors.whitelist.duplicated',
                          ),
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputComponent placeholder="192.168.1.222" />
            </Form.Item>
            <ButtonComponent
              className="api-authentication__btn-add"
              type="primary"
              htmlType="submit"
              icon={<PlusIcon />}
              loading={loadingAddIp}
            >
              {translation('api_page.authentication.add_ip')}
            </ButtonComponent>
          </div>
        </Form>
        {addresses.map((ip, index) => (
          <div
            key={uniqueId()}
            className="api-authentication__whitelist api-authentication__item"
          >
            <p className="mb-0">{ip}</p>
            <TrashIcon
              onClick={() => handleRemoveIP(index)}
              className="api-authentication__icon"
            />
          </div>
        ))}
      </Card>
      <Card className="api-authentication">
        <div className="api-authentication__token">
          <span className="api-authentication__token__label">
            {translation('api_page.authentication.endpoint')}
          </span>
          <span className="api-authentication__token__value">
            {ENV.OPEN_API_BASE_URL}
          </span>
        </div>
        <div className="api-authentication__token">
          <span className="api-authentication__token__label">
            {translation('api_page.authentication.key')}
          </span>
          <span className="api-authentication__token__value">
            {APIKey}
            {APIKey && (
              <CopyIcon
                className="api-authentication__icon"
                onClick={() => copyToken(APIKey)}
              />
            )}
          </span>
        </div>
        <div className="api-authentication__token">
          <span className="api-authentication__token__label" />
          <i className="api-authentication__token__hint">
            {translation('api_page.authentication.warning')}
          </i>
        </div>
        <ButtonComponent
          className="api-authentication__token__button"
          type="primary"
          onClick={() => genAPIKey()}
          loading={loading}
        >
          {translation('api_page.authentication.update_token')}
        </ButtonComponent>
      </Card>
    </>
  );
}

export default Authentication;
